import React, { useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import Modal from "@/components/Modal";
import { recalculateIncentiveReq } from "@/api/projectsApi";
import { formatCurrency } from "@/utils/formatCurrency.ts";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { ProjectRecord } from "@/types";

const FALLBACK_ERROR = "Failed to recalculate the incentive. Please try again.";

/**
 * The API reports domain failures ("no SDCP account data", "no battery capacity") under
 * `error`, which getErrorMessage does not look at — read it first so the admin is told why
 * the recalculation was refused rather than getting a generic status message.
 */
const readErrorMessage = (error: unknown): string => {
  const specific = (
    error as { response?: { data?: { error?: string } } } | undefined
  )?.response?.data?.error;
  return specific || getErrorMessage(error, FALLBACK_ERROR);
};

type Props = {
  projectId: ProjectRecord["id"];
  /** Currently stored incentive, in cents. */
  currentIncentive: number;
  onSuccess?: (newIncentiveInCents: number) => void;
};

/**
 * The deliberate "recalculate" control (SDCP-1305).
 *
 * Saving a project no longer recalculates its incentive, because the customer's CRM NEM/SBP
 * status can have changed since the project was approved and a routine edit would silently
 * restate an already-paid amount. Recalculating is therefore an explicit, admin-only action,
 * and it warns that the result may come out lower before anything is written.
 */
const RecalculateIncentiveModal = ({
  projectId,
  currentIncentive,
  onSuccess,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = () => {
    setError(null);
    setOpen(true);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setOpen(false);
      setError(null);
    }
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await recalculateIncentiveReq(projectId);
      setOpen(false);
      onSuccess?.(response.data.project.upfrontIncentivePayment);
    } catch (err) {
      setError(readErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpen}
        data-testid="recalculate-incentive-button"
        sx={{
          padding: "6px 12px",
          fontSize: "14px",
          whiteSpace: "nowrap",
          color: "#0076CE",
          borderColor: "#0076CE",
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: "rgba(0, 118, 206, 0.04)",
            borderColor: "#0076CE",
          },
        }}
      >
        Recalculate
      </Button>

      <Modal
        open={open}
        onClose={handleClose}
        title="Recalculate Upfront Incentive"
        boxSx={{ maxWidth: 500, width: "100%" }}
      >
        {/* Modal forwards a ref to its single child, so everything lives in one Box. */}
        <Box>
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: "textLightGray3" }}>
              This recalculates the incentive from the project's current system
              values and the account's current CARE / FERA / CoC / NEM-SBP status.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "textLightGray3" }}>
              If the account's status has changed since the project was approved,
              the new amount may be lower than the stored one. The current amount
              is{" "}
              <Typography component="span" variant="body1Medium">
                {formatCurrency(currentIncentive / 100)}
              </Typography>
              .
            </Typography>

            {error && (
              <Typography
                variant="body2"
                data-testid="recalculate-incentive-error"
                sx={{ color: "error.main", mt: 2, fontSize: "14px" }}
              >
                {error}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{
                borderColor: "#D0D5DD",
                color: "#344054",
                "&:hover": {
                  borderColor: "#D0D5DD",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={isSubmitting}
              data-testid="recalculate-incentive-confirm"
              sx={{
                backgroundColor: "#0076CE",
                color: "#fff",
                "&:hover": { backgroundColor: "#0056a3" },
                "&:disabled": { backgroundColor: "#B0B0B0", color: "#fff" },
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
                  Recalculating...
                </>
              ) : (
                "Recalculate"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default RecalculateIncentiveModal;
